import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import alert from '../utils/alertService';
import { createModalHandlers, createButtonHandler, preventBodyScroll } from '../utils/modalUtils';
import './Shop.css';

const Shop = () => {
  usePageTitle('Shop');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderForm, setOrderForm] = useState({
    quantity: 1,
    shipping_address: '',
    phone: '',
    notes: '',
  });

  const categories = [
    { value: 'all', label: 'All', icon: '🛍️' },
    { value: 'medal', label: 'Medals', icon: '🏅' },
    { value: 'sticker', label: 'Stickers', icon: '🎨' },
    { value: 'hoodie', label: 'Hoodies', icon: '🧥' },
    { value: 't_shirt', label: 'T-Shirts', icon: '👕' },
    { value: 'cap', label: 'Caps', icon: '🧢' },
    { value: 'mug', label: 'Mugs', icon: '☕' },
  ];

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      console.log('Fetching products with params:', params);
      const response = await api.get('/products', { params });
      console.log('Products API response:', response);
      console.log('Products response.data:', response.data);
      
      // Handle different response structures
      let productsData = [];
      if (Array.isArray(response.data)) {
        productsData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        productsData = response.data.data;
      } else if (response.data?.products && Array.isArray(response.data.products)) {
        productsData = response.data.products;
      }
      
      console.log('Processed products data:', productsData);
      console.log('Number of products:', productsData.length);
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOrder = (product) => {
    setSelectedProduct(product);
    setOrderForm({
      quantity: 1,
      shipping_address: '',
      phone: '',
      notes: '',
    });
    setShowOrderModal(true);
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    try {
      await api.post('/orders', {
        product_id: selectedProduct.id,
        ...orderForm,
      });
      alert.success('Order placed successfully!');
      setShowOrderModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error placing order:', error);
      alert.error(error.response?.data?.message || 'Error placing order');
    }
  };

  if (loading) {
    return (
      <div className="shop-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="shop-container">
      <div className="shop-header">
        <h1>🛍️ ALIENS SHOP</h1>
        <p className="shop-subtitle">Get your official ALIENS merchandise</p>
      </div>

      <div className="category-filters">
        {categories.map((cat) => (
          <button
            key={cat.value}
            className={`category-btn ${selectedCategory === cat.value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.value)}
          >
            <span className="category-icon">{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="no-products">
          <p>No products available in this category.</p>
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', marginTop: '10px' }}>
            Check the browser console for debugging information.
          </p>
        </div>
      ) : (
        <div className="products-grid">
          {products.map((product) => (
            <div key={product.id} className="product-card">
              <div className="product-image-wrapper">
                {product.image_url || product.image ? (
                  <img
                    src={product.image_url ? `${product.image_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${product.image}?t=${Date.now()}`}
                    alt={product.name}
                    className="product-image"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300x300/0a0e27/00ff88?text=Product';
                    }}
                  />
                ) : (
                  <div className="product-placeholder">
                    <span className="placeholder-icon">🛍️</span>
                  </div>
                )}
                {!product.is_available && (
                  <div className="product-unavailable">Out of Stock</div>
                )}
              </div>
              <div className="product-info">
                {product.category && (
                  <div className="product-category-badge">
                    {categories.find(c => c.value === product.category)?.icon} {product.category.replace('_', ' ').toUpperCase()}
                  </div>
                )}
                <h3 className="product-name">{product.name}</h3>
                {product.description && (
                  <p className="product-description">{product.description}</p>
                )}
                <div className="product-footer">
                  <div className="product-price">${product.price || 0}</div>
                  {product.stock !== null && product.stock !== undefined && (
                    <div className="product-stock">Stock: {product.stock}</div>
                  )}
                </div>
                <button
                  className="btn-order"
                  onClick={() => handleOrder(product)}
                  disabled={!product.is_available || (product.stock !== null && product.stock !== undefined && product.stock === 0)}
                >
                  {product.is_available && (product.stock === null || product.stock === undefined || product.stock > 0)
                    ? '🛒 Order Now'
                    : 'Out of Stock'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showOrderModal && selectedProduct && (
        <div 
          className="modal-overlay" 
          {...createModalHandlers(() => setShowOrderModal(false))}
        >
          <div 
            className="modal-content shop-modal" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close" 
              {...createButtonHandler(() => setShowOrderModal(false))}
            >
              ×
            </button>
            <h2>Place Order</h2>
            <div className="order-product-info">
              <img
                src={selectedProduct.image_url ? `${selectedProduct.image_url}?t=${Date.now()}` : (selectedProduct.image ? `https://aliensteam.com/storage/${selectedProduct.image}?t=${Date.now()}` : 'https://via.placeholder.com/150x150/0a0e27/00ff88?text=Product')}
                alt={selectedProduct.name}
                className="order-product-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/150x150/0a0e27/00ff88?text=Product';
                }}
              />
              <div>
                <h3>{selectedProduct.name}</h3>
                <p className="order-product-price">${selectedProduct.price || 0} each</p>
              </div>
            </div>
            <form onSubmit={handleSubmitOrder} className="order-form">
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  max={selectedProduct.stock && selectedProduct.stock > 0 ? selectedProduct.stock : 999}
                  value={orderForm.quantity}
                  onChange={(e) => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Total Price</label>
                <div className="total-price">
                  ${((selectedProduct.price || 0) * orderForm.quantity).toFixed(2)}
                </div>
              </div>
              <div className="form-group">
                <label>Shipping Address *</label>
                <textarea
                  value={orderForm.shipping_address}
                  onChange={(e) => setOrderForm({ ...orderForm, shipping_address: e.target.value })}
                  required
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={orderForm.phone}
                  onChange={(e) => setOrderForm({ ...orderForm, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowOrderModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Place Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;

